import { client } from 'data-provider';
import { gql } from 'graphql-request';
import { IAvatar, ISort } from 'interfaces';
import { ExportProduct } from './product';

type IPhoto = IAvatar;
export interface ITemplateAttribute {
  attributeId: string;
  attributeName: string;
  attributeType: 'text' | 'float' | 'boolean' | 'dropdown';
  value: string;
}

export interface IVariant {
  productVariantId: string;
  productVariantName: string;
  sku: string;
  description: string | null;
  purchasePrice: number;
  sellingPrice: number;
  productId: string;
  productName: string;
  tags: { id: string; name: string }[];
  category: { id: string; name: string } | null;
  warehouses: { id: string; name: string }[];
  productVariantPhoto?: IPhoto;
  productTemplates: {
    templateId: string;
    templateName: string;
    templateType: 'template' | 'variant';
    productTemplateAttributes: ITemplateAttribute[];
  }[];
  customFields: { [key: string]: string }[] | null;
}

export interface IUpdateVariantData {
  name: string;
  sku: string;
  description: string;
  productCategory: string | null;
  productTags: string[];
  productWarehouses: string[];
  templateIds: string[];
  customFields: { key: string; value: string }[];
  variantAttributes: {
    attributeId: string;
    value: string;
  }[];
  photo?: any;
}

export interface ImportVariantData {
  productName: string;
  productSku: string;
  purchasePrice: number;
  sellingPrice: number;
  category: string;
  tags: string[];
  metadataFields: { key: string; type: 'text' }[];
  variants: {
    variantName: string;
    variantSku: string;
    purchasePrice: number;
    sellingPrice: number;
    category: string;
    tags: string[];
    metadata: { key: string; value: string }[];
  }[];
}

interface CreateVariantData {
  productId: string;
  name: string;
  sku: string;
  description?: string;
  purchasePrice: number;
  sellingPrice: number;
  productCategory: string | null;
  productTags: string[];
  productWarehouses: string[];
  templateIds: string[];
  variantAttributes: {
    attributeId: string;
    value: string;
  }[];
  customFields: { key: string; value: string }[];
  photo?: IPhoto;
}

export const createVariant = async (data: CreateVariantData) => {
  const query = gql`
    mutation ($input: createProductVariantInput!) {
      createProductVariant(input: $input) {
        productId
        productName
        sku
        description
      }
    }
  `;

  const variables = { input: { data } };
  const savedProduct = await client.request(query, variables);
  return savedProduct;
};

export const getVariants = async (query?: {
  start?: number;
  limit?: number;
  searchString?: string;
  productType?: string;
  sort?: ISort | null;
}) => {
  const gqlQuery = gql`
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      productVariants(where: $where, sort: $sort, start: $start, limit: $limit) {
        page
        size
        total
        data {
          productVariantId
          productVariantName
          sku
          type
          description
          purchasePrice
          sellingPrice
          productId
          productName
          customFields
          tags {
            id
            name
          }
          category {
            id
            name
          }
          warehouses {
            id
            name
          }
          productTemplates {
            templateId
            templateName
            productTemplateAttributes {
              attributeId
              attributeName
              attributeType
              value
            }
          }
          productVariantPhoto {
            url
            thumbUrl
          }
        }
      }
    }
  `;

  const variables = {
    where: {
      query: query && query.searchString ? query.searchString : '',
      type: query && query.productType ? query.productType : undefined,
    },
    sort: query?.sort,
    start: query && query.start ? query.start : 1,
    limit: query && query.limit ? query.limit : 10,
  };
  const data = await client.request(gqlQuery, variables);
  return data.productVariants as {
    data: IVariant[];
    page: number;
    size: number;
    total: number;
  };
};

export const getVariantsDropdown = async (query?: { sku: string[] }) => {
  const where = query && query.sku && Array.isArray(query.sku) ? { sku: query.sku } : null;

  const gqlQuery = gql`
    query ($where: JSON, $sort: SortInput) {
      productVariantsDropdown(where: $where, sort: $sort) {
        productVariantId
        productVariantName
        sku
        description
        purchasePrice
        sellingPrice
        productId
        productName
        customFields
        tags {
          id
          name
        }
        category {
          id
          name
        }
        productTemplates {
          templateId
          templateName
          productTemplateAttributes {
            attributeId
            attributeName
            attributeType
            value
          }
        }
        productVariantPhoto {
          url
          thumbUrl
        }
      }
    }
  `;

  const variables = { where };
  const data = await client.request(gqlQuery, variables);
  return data.productVariantsDropdown as IVariant[];
};

export const getVariant = async (id: string) => {
  const gqlQuery = gql`
    query ($productVariantId: ID!) {
      productVariant(id: $productVariantId) {
        productVariantId
        productVariantName
        sku
        description
        purchasePrice
        sellingPrice
        productId
        productName
        customFields
        tags {
          id
          name
        }
        category {
          id
          name
        }
        warehouses {
          id
          name
        }
        productTemplates {
          templateId
          templateName
          productTemplateAttributes {
            attributeId
            attributeName
            attributeType
            value
          }
        }
        productVariantPhoto {
          thumbUrl
          url
        }
      }
    }
  `;

  const variables = {
    productVariantId: id,
  };
  const data = await client.request(gqlQuery, variables);
  return data.productVariant as IVariant;
};

export const updateVariant = async (id: string, data: IUpdateVariantData) => {
  const query = gql`
    mutation ($input: UpdateProductVariantInput!) {
      updateProductVariant(input: $input) {
        productVariantId
      }
    }
  `;

  const variables = { input: { where: { id }, data } };
  const updatedData = await client.request(query, variables);
  return updatedData.updateProductVariant as { productVariantId: string };
};

export const updateVariantOnRow = async (
  id: string,
  data: {
    sku: string;
    purchasePrice: number;
    sellingPrice: number;
  }
) => {
  const query = gql`
    mutation ($input: UpdateOnRowProductVariantInput!) {
      updateOnRowProductVariant(input: $input) {
        productVariantId
      }
    }
  `;

  const variables = { input: { where: { id }, data } };
  const updatedData = await client.request(query, variables);
  return updatedData.updateOnRowProductVariant as { productVariantId: string };
};

export const deleteVariant = async (id: string) => {
  const mutation = gql`
    mutation ($productVariantId: ID!) {
      deleteProductVariant(productVariantId: $productVariantId) {
        productVariant {
          id
        }
      }
    }
  `;

  const variables = {
    productVariantId: id,
  };
  const response = await client.request(mutation, variables);
  return response.deleteProductVariant as { id: string };
};

export const getVariantColumnNames = async () => {
  const query = gql`
    query {
      getVariantColumnNames {
        uniqueAttributeNames
        uniqueCustomFieldNames
      }
    }
  `;

  const response = await client.request(query);
  return response.getVariantColumnNames as {
    uniqueAttributeNames: string[];
    uniqueCustomFieldNames: string[];
  };
};

export const importVariants = async (data: ImportVariantData[]) => {
  const mutation = gql`
    mutation ($input: createCSVBulkProductVariantInput!) {
      importCSVProductCopy(input: $input)
    }
  `;

  const variables = {
    input: { data },
  };

  const createdData = await client.request(mutation, variables);
  return createdData;
};

export const exportVariants = async (query?: string, sort?: ISort | null) => {
  const gqlQuery = gql`
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      exportCSVProductVariants(where: $where, sort: $sort, start: $start, limit: $limit) {
        data {
          id
          name
          sku
          purchasePrice
          sellingPrice
          photo {
            thumbUrl
            url
          }
          tags {
            id
            name
          }
          category {
            id
            name
          }
          description
          customFields
          variants {
            id
            name
            sku
            purchasePrice
            sellingPrice
            measurementUnit {
              id
              name
            }
            make
            sell
            purchase
            type
            photo {
              thumbUrl
              url
            }
            tags {
              id
              name
            }
            category {
              id
              name
            }
            description
            customFields
            variantAttributes
          }
        }
      }
    }
  `;

  const variables = {
    where: { query: query ?? '' },
    sort,
  };
  const data = await client.request(gqlQuery, variables);
  return (data.exportCSVProductVariants?.data ?? []) as ExportProduct[];
};
